/**
 * AuthorFollowCard — prominent "Follow this writer" surface for
 * article pages. Wraps SubscribeButton with author identity + name
 * so readers actually convert on the strongest possible signal.
 *
 * Two visual variants:
 *   • variant="inline"  — compact byline follow (small avatar + name + button)
 *   • variant="card"    — full card at the article footer (large avatar + follow + share hint)
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SubscribeButton from "@/components/publisher/SubscribeButton";

const BASE = `${process.env.REACT_APP_BACKEND_URL || ""}/api/publisher/v1`;

function getToken() {
  if (typeof window === "undefined") return null;
  return window.localStorage.getItem("sp_token") || window.localStorage.getItem("token");
}

function useAuthor(authorId) {
  const [state, setState] = useState({ author: null, follower_count: 0, loading: true });
  useEffect(() => {
    if (!authorId) return;
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${BASE}/authors/${authorId}/card`);
        if (!r.ok) throw new Error();
        const d = await r.json();
        if (alive) setState({
          author: d.author || null,
          follower_count: d.follower_count || 0,
          loading: false,
        });
      } catch {
        if (alive) setState({ author: null, follower_count: 0, loading: false });
      }
    })();
    return () => { alive = false; };
  }, [authorId]);
  return state;
}

function initials(name) {
  return (name || "?").split(/\s+/).slice(0, 2).map((w) => w[0] || "").join("").toUpperCase();
}

export default function AuthorFollowCard({ authorId, variant = "inline", fallbackName = "" }) {
  const { author, follower_count, loading } = useAuthor(authorId);
  const token = getToken();
  const isSelf = token && author?.is_me;

  if (!authorId) return null;

  // Prefer author record → then explicit fallback (e.g. article's
  // author_display_name) → then a friendly "SocialPulse Editorial"
  // when the id is a raw UUID. Never expose the raw UUID.
  const looksLikeUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(authorId);
  const displayName = author?.name
    || fallbackName
    || (looksLikeUuid ? "SocialPulse Editorial" : authorId);
  const handle = author?.handle ? `@${author.handle}` : null;
  // Prefer the beautiful /{handle} SocialPulse profile if we know it,
  // otherwise fall back to the /publisher/author/{id} legacy route.
  const profileHref = author?.handle ? `/${author.handle}?tab=articles` : `/publisher/author/${authorId}`;

  if (variant === "inline") {
    return (
      <div
        className="inline-flex items-center gap-2.5 text-xs"
        data-testid="author-follow-card-inline"
      >
        <Link
          to={profileHref}
          className="inline-flex items-center gap-2 group"
          data-testid="author-follow-card-inline-link"
        >
          {author?.photo_url ? (
            <img
              src={author.photo_url}
              alt=""
              className="w-8 h-8 rounded-full object-cover ring-1 ring-slate-200"
            />
          ) : (
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-teal-500 to-blue-600 text-white text-[10px] font-bold flex items-center justify-center">
              {initials(displayName)}
            </div>
          )}
          <span className="flex flex-col leading-tight">
            <span className="font-semibold text-slate-900 group-hover:text-blue-700">
              {displayName}
            </span>
            {follower_count > 0 && (
              <span className="text-[10px] text-slate-500">
                {follower_count.toLocaleString()} follower{follower_count === 1 ? "" : "s"}
              </span>
            )}
          </span>
        </Link>
        {!isSelf && !loading && (
          <SubscribeButton
            targetType="author"
            targetId={authorId}
            label="Follow"
            size="sm"
            testId="author-follow-inline-btn"
          />
        )}
      </div>
    );
  }

  // card variant — big footer surface
  return (
    <aside
      className="mt-10 p-6 rounded-2xl border border-slate-200 bg-gradient-to-br from-white to-slate-50 flex flex-col sm:flex-row items-start sm:items-center gap-4"
      data-testid="author-follow-card"
    >
      <Link to={profileHref} className="shrink-0" data-testid="author-follow-card-link">
        {author?.photo_url ? (
          <img
            src={author.photo_url}
            alt=""
            className="w-16 h-16 rounded-full object-cover ring-2 ring-white shadow-md"
          />
        ) : (
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-teal-500 to-blue-600 text-white text-lg font-bold flex items-center justify-center ring-2 ring-white shadow-md">
            {initials(displayName)}
          </div>
        )}
      </Link>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
          Written by
        </p>
        <Link
          to={profileHref}
          className="block text-lg font-bold text-slate-900 hover:text-blue-700 leading-tight"
          data-testid="author-follow-card-name"
        >
          {displayName}
        </Link>
        {handle && (
          <p className="text-xs text-slate-500">{handle}</p>
        )}
        {author?.bio && (
          <p className="text-sm text-slate-600 mt-1 line-clamp-2">{author.bio}</p>
        )}
        {follower_count > 0 && (
          <p className="text-xs text-slate-500 mt-1">
            <span className="font-semibold text-slate-800">{follower_count.toLocaleString()}</span>{" "}
            follower{follower_count === 1 ? "" : "s"} on SocialPulse
          </p>
        )}
      </div>
      {!isSelf && !loading && (
        <SubscribeButton
          targetType="author"
          targetId={authorId}
          label="Follow this writer"
          size="md"
          testId="author-follow-card-btn"
        />
      )}
      {isSelf && (
        <Link
          to="/publisher/dashboard"
          className="text-xs font-semibold text-blue-700 hover:underline"
          data-testid="author-follow-card-self"
        >
          Your dashboard →
        </Link>
      )}
    </aside>
  );
}
